import * as R from 'ramda';
import React, { useState } from 'react';
import { Button, Icon, Input, Table } from 'antd';
import CcnUpdateEnabledMutation from '../../../mutations/CcnUpdateEnabledMutation';
import { message } from 'antd';
import GenerateSpidClientUrlMutation from '../../../mutations/GenerateSpidClientUrlMutation';

const { Column } = Table;

const onUpdateEnabled = (ccnId, scope, offerType, enabled, setLoading) => {
  setLoading(true);

  CcnUpdateEnabledMutation({
    ccnId,
    scope,
    enabled,
    offerType
  }, (ok, error) => {
    if (error) console.log(error);
    setLoading(false);

    if (ok) {
      message.success(`La ccn a été ${enabled ? 'activée' : 'désactivée'}.`);
    } else {
      message.error('Une erreur est survenue, veuillez réessayer.');
    }
  });
};

const EnablingButtonsWrapper = ({ children }) => {
  return <div
    style={{
      display: 'flex',
      alignItems: 'center',
      columnGap: '5px'
    }}
  >
    {children}
  </div>;
};

const ccnHasScope = (scope, offerType) => R.compose(
  R.not,
  R.isNil,
  R.find(
    R.whereEq({
      scope,
      offerType
    })
  )
);

const EnablingButton = ({
  ccnId,
  scope,
  offerType,
  scopeLabel,
  status
}) => {
  const [loading, setLoading] = useState(false);

  const hasScope = ccnHasScope(scope, offerType)(status);

  if (!hasScope) {
    return null;
  }

  const enabled = R.compose(
    R.propOr(false, 'enabled'),
    R.defaultTo({}),
    R.find(R.whereEq({ scope, offerType }))
  )(status);

  return <Button
    type={enabled ? 'danger' : 'primary'}
    loading={loading}
    onClick={() => onUpdateEnabled(ccnId, scope, offerType, !enabled, setLoading)}
    style={{ minWidth: '100px' }}
  >
    {enabled ? 'Désactiver' : 'Activer'} {scopeLabel}
  </Button>;
};

const compareString = (a, b, prop) => R.propOr('', prop)(a).localeCompare(R.propOr('', prop)(b));

const CcnTable = ({
  ccns,
  loading
}) => {
  const openSpidClient = (ccnId, offerTypes) => () => {
    GenerateSpidClientUrlMutation({ ccnId, offerTypes }, (ok, error, url) => {
      if (ok && !error) {
        window.open(url, '_blank');
      } else {
        message.error('Une erreur est survenue, veuillez réessayer.');
      }
    });
  };

  const [filters, setFilters] = useState({
    searchText: '',
    searchedColumn: ''
  });

  const handleSearch = (selectedKeys, confirm, dataIndex, clearFilters) => {
    confirm();
    setFilters({
      searchText: selectedKeys[0],
      searchedColumn: dataIndex
    });
  };

  const handleReset = clearFilters => {
    clearFilters();
    setFilters({ searchText: '' });
  };

  const onFilter = dataIndex => (value, record) => {
    return R.compose(
      R.includes(R.toLower(value)),
      R.toLower,
      R.propOr('', dataIndex)
    )(record);
  };

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder="Rechercher..."
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 'full', marginBottom: 8, display: 'block' }}
        />

        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex, clearFilters)}
          icon="search"
          size="small"
          style={{ width: 120, marginRight: 8 }}
        >
          Rechercher
        </Button>

        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 100 }}>
          Réinitialiser
        </Button>
      </div>
    ),
    filterIcon: filtered => <Icon type="search" style={{ color: filtered ? '#1890ff' : undefined }}/>,
    onFilter: onFilter(dataIndex)
  });

  return <Table
    dataSource={ccns}
    loading={loading}
    locale={{ emptyText: 'Aucune CCN n\'a été trouvée.' }}
    pagination={{
      position: 'both',
      size: 'small',
      style: {
        width: '100%',
        justifyContent: 'center',
        display: 'flex'
      }
    }}
    rowKey="ccnId"
  >
    <Column
      title="ID"
      dataIndex="ccnId"
      sorter={(a, b) => compareString(a, b, 'ccnId')}
      {...getColumnSearchProps('ccnId')}
    />

    <Column
      title="CCN"
      dataIndex="shortTitle"
      sorter={(a, b) => compareString(a, b, 'shortTitle')}
      {...getColumnSearchProps('shortTitle')}
    />

    <Column
      title="SPID"
      dataIndex="actionEnable"
      render={(text, { ccnId, status }) => <EnablingButtonsWrapper>
        <EnablingButton
          ccnId={ccnId}
          scope="SPID"
          offerType="PREVOYANCE"
          scopeLabel="Prévoyance"
          status={status}
        />

        <EnablingButton
          ccnId={ccnId}
          scope="SPID"
          offerType="SANTE"
          scopeLabel="Santé"
          status={status}
        />
      </EnablingButtonsWrapper>}
      width="100px"
    />

    <Column
      title="SPID Client Espace Partenaire"
      dataIndex="actionEnableClient"
      render={(text, { ccnId, status }) => <EnablingButtonsWrapper>
        <EnablingButton
          ccnId={ccnId}
          scope="SPID_CLIENT"
          offerType="PREVOYANCE"
          scopeLabel="Prévoyance"
          status={status}
        />

        <EnablingButton
          ccnId={ccnId}
          scope="SPID_CLIENT"
          offerType="SANTE"
          scopeLabel="Santé"
          status={status}
        />
      </EnablingButtonsWrapper>}
      width="100px"
    />

    <Column
      title="URL"
      dataIndex="spidClient"
      render={(text, { ccnId, status }) => <div
        style={{
          columnGap: '5px'
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center'
        }}>
          {ccnHasScope('SPID_CLIENT', 'PREVOYANCE')(status) && <Button
            onClick={openSpidClient(ccnId, ['PREVOYANCE'])}
            style={{
              backgroundColor: '#22b14c',
              color: 'white'
            }}
          >
            Souscrire en ligne Prévoyance
          </Button>}

          {ccnHasScope('SPID_CLIENT', 'SANTE')(status) && <Button
            onClick={openSpidClient(ccnId, ['SANTE'])}
            style={{
              backgroundColor: '#22b14c',
              color: 'white'
            }}
          >
            Souscrire en ligne Santé
          </Button>}
        </div>

        {ccnHasScope('SPID_CLIENT', 'PREVOYANCE')(status) && ccnHasScope('SPID_CLIENT', 'SANTE')(status) && <Button
          onClick={openSpidClient(ccnId, ['SANTE', 'PREVOYANCE'])}
          style={{
            backgroundColor: '#22b14c',
            color: 'white'
          }}
        >
          Souscrire en ligne Santé et Prévoyance
        </Button>}
      </div>}
      width="100px"
    />
  </Table>;
};

export default CcnTable;
